import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbActiveModal, NgbModal} from '@ng-bootstrap/ng-bootstrap';
import { OperateursService } from '../../../../../../../data/intervenant/services/operateurs/operateurs.service';
import { DomaineSpec } from '../../../../../../../data/intervenant/models/domaine-spec.model';
import {InformationMajeure} from "../../../../../../../data/intervenant/models/information-majeure.model";
import {CommentaireModificationComponent} from "../commentaire-modification/commentaire-modification.component";
import {ModificationMajeure} from "../../../../../../../data/intervenant/models/modification-majeure.model";
import {TypeModificationEnum} from "../../../../../../../data/intervenant/models/enums/type-modification.enum";
import {switchMap} from "rxjs/operators";
import {SessionContext} from "../../../../../../../core/services/config/app.settings";


@Component({
    selector: 'app-edit-informations-majeures',
    templateUrl: './edit-informations-majeures.component.html',
    styleUrls: ['./edit-informations-majeures.component.scss'],
    standalone: false
})
export class EditInformationsMajeuresComponent implements OnInit {
    @Input() idOperateur: number;
    @Input() informationsMajeures: any;
    @Input() operateur: any;
    @Input() infosDomaine: any[];

    domaineSpecs: DomaineSpec[] = [];
    formInformationsMajeures: UntypedFormGroup;

    /**
     * Initial form values
     */
    initialFormValue: InformationMajeure;

    /**
     * Getter Id intervenant
     */
    get idIntervenant(): number {
      return SessionContext.get('idIntervenant');
    }

    /**
     * Getter id organisme
     */
    get idOrganisme(): number {
      return SessionContext.get('idOrganisme');
    }

    get siret() {
        return this.formInformationsMajeures.get('siret');
    }

    constructor(private readonly fb: UntypedFormBuilder,
                private readonly activeModal: NgbActiveModal,
                private readonly modal: NgbModal,
                private operateurService: OperateursService) {}

    ngOnInit() {
        this.initForm();
        this.loadDomaineSpecs();
    }

    initForm() {
        this.formInformationsMajeures = this.fb.group({
            structureJuridique: [this.operateur.structureJuridique || ''],
            raisonSociale: [this.operateur.raisonSociale || '', Validators.required],
            siret: [this.operateur.siret || '', [Validators.required, Validators.minLength(14), Validators.maxLength(14)]],
            tvaIntracommunautaire: [this.operateur.tvaIntracommunautaire || ''],
            domaineSpecs: this.fb.group({})
        });
    }

    private loadDomaineSpecs() {
        this.operateurService.getAllInformationsDomaine().subscribe(
            specs => {
                this.domaineSpecs = specs;
                this.addDomaineSpecsToForm();
            },
            error => console.error('Erreur lors du chargement des spécifications de domaine', error)
        );
    }

    private addDomaineSpecsToForm() {
        const domaineSpecsGroup = this.formInformationsMajeures.get('domaineSpecs') as UntypedFormGroup;
        this.domaineSpecs.forEach(spec => {
            // Recherche de la valeur existante dans infosDomaine
            const existingInfo = this.infosDomaine.find(info => info.code === spec.code);
            const existingValue = existingInfo ? existingInfo.valeur : '';

            const control = this.fb.control(existingValue);
            if (spec.code === 'VIN_CVI') {
                control.setValidators([Validators.required, Validators.minLength(10), Validators.maxLength(10)]);
            }
            domaineSpecsGroup.addControl(spec.code, control);
        });

        // Initialise le snapshot initial une fois tous les contrôles ajoutés
        this.initialFormValue = this.formInformationsMajeures.getRawValue();
        this.formInformationsMajeures.markAsPristine();
    }

    onSubmit() {
      const currentValue = this.formInformationsMajeures.getRawValue();
      const hasDiff = this.hasFormChanged(this.initialFormValue, currentValue);

      // Prépare les données pour le PUT updateInformationsMajeures
      const toUpdatedPayload = (value: InformationMajeure) => {
        const domaineSpecsData = value.domaineSpecs || {};
        const payload: any = {
          ...value,
          intervenantDomaineSpecs: Object.keys(domaineSpecsData).map(code => ({
            code: (this.domaineSpecs.find(spec => spec.code === code) || { code }).code,
            valeur: domaineSpecsData[code]
          }))
        };
        delete payload.domaineSpecs;
        return payload;
      };

      const updatedData = toUpdatedPayload(currentValue);

      // Si aucune modification : on met à jour directement sans commentaire/modification majeure
      if (!hasDiff) {
        this.operateurService.updateInformationsMajeures(this.idOperateur, updatedData).subscribe(
          () => this.activeModal.close('informations_majeures_updated'),
          error => console.error('Erreur lors de la mise à jour des informations majeures', error)
        );
        return;
      }

      // Modifications détectées : ouvrir la modale de commentaire puis ajouter la modification majeure et faire la mise à jour
      const modalRef = this.modal.open(CommentaireModificationComponent, { backdrop: 'static', size: 'lg' });
      modalRef.result.then((result) => {
        // Construction de l'objet ModificationMajeure
        const oldPayload = toUpdatedPayload(this.initialFormValue);
        const modificationMajeure: ModificationMajeure = {
          idOperateur: this.idOperateur,
          dateModification: new Date(),
          idAuteur: this.idIntervenant,
          idOrganisme: this.idOrganisme,
          idModificationType: TypeModificationEnum.MAJEURE,
          oldRow: oldPayload,
          newRow: updatedData,
          commentaire: result && result.commentaire ? result.commentaire : ''
        };

        // Mise à jour des informations majeures et enregistrement de la modification
        this.operateurService.updateInformationsMajeures(this.idOperateur, updatedData).pipe(
          switchMap(() => this.operateurService.addModification(modificationMajeure))
        ).subscribe(
          () => this.activeModal.close('informations_majeures_updated'),
          error => console.error('Erreur lors de la mise à jour des informations majeures', error)
        );
      }, () => {
        // Dismissed: ne rien faire
      });
    }

  private hasFormChanged(initial: any, current: any): boolean {
    // Comparaison profonde simple basée sur JSON
    try {
      return JSON.stringify(initial) !== JSON.stringify(current);
    } catch {
      return true;
    }
  }

    onSiretBlur() {
        this.siret.markAsTouched();
    }
    onCviBlur(code: string) {
        const control = this.formInformationsMajeures.get('domaineSpecs').get(code);
        if (control) {
            control.markAsTouched();
        }
    }

    onClose() {
        this.activeModal.dismiss();
    }
}
